<script setup>
import {reactive, onMounted, watch, ref} from "vue";
import {ApiWrapper} from "@/Utils/ApiWrapper";
import {Permissions} from "@/Utils/Permissions";
import {ToastUtil} from "@/Utils/ToastUtil";


const data = reactive({
  batches: [],
  checkDate: null,
  checkNumber: null,
  dataLoading: true,
  closeCheckDate: false,
  headers: [
    { title: 'Invoice Number', key: 'invoicenumber' },
    { title: 'Invoice Date', key: 'invoicedate' },
    { title: 'Claim Number', key: 'claimnumber' },
    { title: 'Client Name', key: 'clientname' },
    { title: 'Amount', key: 'total' },
    { title: 'Pending', key: 'pending' },
    { title: 'Check Date', key: 'checkdate', sortable: false, minWidth: '250px', width: '250px' },
    { title: 'Pay', key: 'pay', sortable: false, width: '75px' },
    { title: 'Check Number', key: 'checknumber', sortable: false, minWidth: '200px' },
    { title: 'Claim Num', key: 'claimnumber', align: ' d-none'}
  ],
  items: [],
  loading: false,
  menu: false,
  menu1: false,
  newRule: false,
  search: '',
  selectedBatch: null,
  selectedYear: null,
  valid: true,
  yearRange: []
});

const form = ref(null);

function getYearRange(startYear = 2010) {
  const currentYear = new Date().getFullYear();
  for (let i = startYear; i <= currentYear; i++) {
    data.yearRange.push(i.toString());
  }
  data.selectedYear = data.yearRange[data.yearRange.length-1];
}

const formatISODateToAmerican = (date) => {
  if (!date) return null
  const [year, month, day] = date.split('-')
  return `${month}/${day}/${year}`
}

const getData = () => {
  data.loading = true;
  data.dataLoading = true;

  // let contextId = 'batchdashboard';
  ApiWrapper.get('payments', 'get-batches','contextId=null')
      .then((response) => {
        let batchRes = response.rows;
        for (let batch in batchRes) {
          if(batchRes[batch].title === null){
            data.batches.push('Title Not Found')
          }else{
            data.batches.push(batchRes[batch].title);
          }
        }
      })
      .catch((err) => {
        ToastUtil.error(err);
      })
  ApiWrapper.post('payments', 'get-data', {
        selectedYear: data.selectedYear
      })
      .then((response) => {
        if(response.rows){
          data.items = response.rows;
        }
      })
      .catch((e)=>{
        ToastUtil.error(e);
      })
      .finally(()=>{
        data.loading = false;
        data.dataLoading = false;
      })
}

const submit = () => {
  if (form.value.validate()) {
    data.loading = true;
    ApiWrapper.post('payments', 'save-payments', {
          batch: data.selectedBatch,
          checkDate: data.checkDate,
          checkNumber: data.checkNumber,
          payments: data.items.filter((item) => item.payment && item.payment != 0)
        })
        .then((response) => {
          getData();
          ToastUtil.successMsg('Created payments');
        })
        .catch((err) => {
          ToastUtil.error(err);
          data.loading = false;
        })
  }
}

onMounted(()=>{
  getYearRange();
  getData();
})
</script>

<template>
  <v-container fluid>
    <v-card class="elevation-1">
      <v-toolbar flat color="white">
        <v-toolbar-title>Payments</v-toolbar-title>
        <v-progress-linear
            :active="data.loading"
            :indeterminate="data.loading"
            absolute
            bottom
            color="primary"
        >
        </v-progress-linear>
        <v-divider class="mx-4" inset vertical></v-divider>
        <v-spacer></v-spacer>
        <v-select
            class="pt-5"
            label="Please select a year to load invoices"
            :items="data.yearRange"
            v-model="data.selectedYear"
            @update:modelValue="data.selectedYear=$event;getData()"
        >
        </v-select>
      </v-toolbar>

      <v-form ref="form" v-model="data.valid" lazy-validation>
        <v-row>
          <v-col cols="12" sm="4">
            <!-- <v-select
              v-model="selectedBatch"
              :items="batches"
              class="ml-4"
              label="Select Batch"
              :rules="[v => !!v || 'Batch is required']"
            ></v-select> -->
            <v-autocomplete
                class="px-4"
                v-model="data.selectedBatch"
                :items="data.batches"
                label="Select Batch"
                :rules="[v => !!v || 'Batch is required']"
                clearable
                :tabindex="1"
            ></v-autocomplete>
          </v-col>
          <v-col cols="12" sm="3">
            <v-text-field
                label="Check Number"
                type="text"
                v-model="data.checkNumber"
                :rules="[v => !!v || 'Check number is required']"
                :tabindex="2"
            ></v-text-field>
          </v-col>
          <v-col cols="12" sm="4">
            <v-menu
                ref="menu"
                v-model="data.menu"
                :close-on-content-click="data.closeCheckDate"
                transition="scale-transition"
                offset-y
                min-width="300px"
            >
              <template v-slot:activator="{ on, props }">
                <v-text-field
                    v-model="data.checkDate"
                    label="Check Date"
                    class="px-5"
                    v-bind="props"
                    v-on="on"
                    clearable
                    readonly
                    prepend-inner-icon="mdi-calendar"
                    :rules="[v => !!v || 'Check date is required']"
                    @focus="data.closeCheckDate = false"
                ></v-text-field>
              </template>
              <v-date-picker
                  v-model="data.checkDate"
                  no-title
                  :show-current="true"
                  @update:modelValue="data.menu = false; data.closeCheckDate = true"
                  :tabindex="3"
              ></v-date-picker>
              <!-- <v-date-picker
                v-model="checkDate"
                no-title
                :show-current="true"
                @input="$refs.menu.save(formatISODateToAmerican(checkDate))"
              ></v-date-picker> -->
            </v-menu>
          </v-col>
        </v-row>
      </v-form>

      <v-row>
        <v-col cols="12" sm="4">
          <v-text-field
              label="Search Payments"
              type="text"
              v-model="data.search"
              append-inner-icon="mdi-magnify"
              class="ml-4"
              :tabindex="4"
          ></v-text-field>
        </v-col>
      </v-row>

      <v-data-table
          :headers="data.headers"
          :items="data.items"
          :search="data.search"
          :loading="data.dataLoading"
          loading-text="Loading... Please wait"
          :header-props="{tabIndex: -1}"
      >
        <template v-slot:[`item.invoicedate`]="{ item }">
          <span>{{ item.invoicedateformatted }}</span>
        </template>
        <template v-slot:[`item.claimnumber`]="{ item }">
          <RouterLink :to="`/claim/${item.claimid}`" :tabindex="-1">
            {{ item.claimnumber}}
          </RouterLink>
        </template>
        <template v-slot:[`item.total`]="{ item }">
          <span>{{ item.totalFormatted }}</span>
        </template>
        <template v-slot:[`item.pending`]="{ item }">
          <span>{{ item.pendingFormatted }}</span>
        </template>
        <template v-slot:[`item.pay`]="{ item, index }">
          <v-number-input
              v-model="item.payment"
              class="pa-2"
              min-width="200px"
              control-variant="hidden"
              :precision="2"
              :tabindex="5 + (index*3 + 2)"
              prepend-inner-icon="mdi-currency-usd"
          ></v-number-input>
        </template>
        <template v-slot:[`item.checknumber`]="{ item, index }">
          <v-text-field
              v-model="item.checknumber"
              class="pa-2"
              type="text"
              :tabindex="5 + (index*3 + 3)"
          ></v-text-field>
        </template>
        <!-- <v-text-field
          v-model="item.payment"
          density="compact"
          class="pa-2"
          filled
          type="number"
          :rules="[v => !!v || 'Pay amount is required']"
        ></v-text-field>
      </template>
      <template v-slot:item.checknumber="{ item }">
        <v-text-field
          v-model="item.checknumber"
          density="compact"
          class="pa-2"
          filled
          type="text"
          :rules="[v => !!v || 'Check number is required']"
        ></v-text-field>
      </template> -->
        <template v-slot:[`item.checkdate`]="{ item }">
          <v-menu
              ref="item.menu1"
              v-model="item.menu1"
              :close-on-content-click="false"
              transition="scale-transition"
              offset-y
              max-width="290px"
              min-width="auto"
          >
            <template v-slot:activator="{ on, props }">
              <v-text-field
                  v-model="item.checkdate"
                  label="Date"
                  persistent-hint
                  clearable
                  readonly
                  prepend-inner-icon="mdi-calendar"
                  v-bind="props"
                  v-on="on"
              ></v-text-field>
            </template>
            <v-date-picker
                v-model="item.checkdate"
                no-title
                @input="item.menu1 = false"
            ></v-date-picker>
          </v-menu>
        </template>
      </v-data-table>

      <v-row class="px-2">
        <v-col cols="12" sm="2">
          <v-btn
              color="primary"
              class="white--text ml-2 mb-2"
              v-if="Permissions.checkPermissions(['payments.save'])"
              :disabled="!data.valid"
              @click="submit"
          >Submit Payments</v-btn
          >
        </v-col>
      </v-row>
    </v-card>
  </v-container>
</template>

<style scoped>
.container {
  padding: 5px;
}
[v-cloak] {
  display: none;
}
</style>